import React from 'react';
import { Phone, AlertTriangle, CheckCircle, FileText, Shield, Scale, Users } from 'lucide-react';
export function CdlTestingInfoPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1891">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1892">
        <div className="container mx-auto px-4" data-id="element-1893">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1894">
            Michigan Commercial Driver License (CDL)
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-1895">
            Federal Requirements, Testing & Disqualifications
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1896">
        <div className="max-w-4xl mx-auto" data-id="element-1897">
          {/* Background */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1898">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1899">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-1900" />
              Commercial Motor Vehicle Safety Act of 1986
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-1901">
              The Commercial Motor Vehicle Safety Act of 1986 was signed into
              law on October 27, 1986. The goal of the Act is to improve highway
              safety by ensuring that drivers of large trucks and buses are
              qualified to operate those vehicles and to remove unsafe and
              unqualified drivers from the highways.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-1902">
              <p className="text-gray-800" data-id="element-1903">
                The Act retained the State's right to issue a driver's license,
                but established <strong data-id="element-1904">minimum national standards</strong>{' '}
                which States must meet when licensing CMV drivers.
              </p>
            </div>

            <p className="text-gray-700" data-id="element-1905">
              It is important to note that the Act does not require drivers to
              obtain a separate Federal license; it merely requires States to
              upgrade their existing testing and licensing programs to conform
              with Federal minimum standards.
            </p>
          </div>

          {/* CDL Exceptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1906">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1907">
              CDL Exceptions
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-1908">
              Michigan offers exceptions to the CDL requirements, which include:
            </p>

            <div className="space-y-4" data-id="element-1909">
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-1910">
                <p className="text-gray-800" data-id="element-1911">
                  • Active military duty operating military vehicles
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-1912">
                <p className="text-gray-800" data-id="element-1913">
                  • Police and firefighters operating authorized emergency
                  vehicles
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-1914">
                <p className="text-gray-800" data-id="element-1915">
                  • Farmers operating within a 150-mile radius of a farm (an "F"
                  endorsement may be required)
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-1916">
                <p className="text-gray-800" data-id="element-1917">
                  • Individuals operating motor homes or vehicles used
                  exclusively to transport personal possessions or family
                  members for nonbusiness purposes
                </p>
              </div>
            </div>
          </div>

          {/* CDL Groups */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1918">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1919">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-1920" />
              What Type of CDL Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-1921">
              A Michigan resident needs a valid operator or chauffeur license
              with the appropriate group designation to operate the following
              vehicles:
            </p>

            <div className="space-y-6" data-id="element-1922">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1923">
                <h3 className="text-2xl font-bold text-blue-600 mb-3" data-id="element-1924">
                  Group A
                </h3>
                <p className="text-gray-700" data-id="element-1925">
                  • A combination of vehicles with a gross combination weight
                  rating of <strong data-id="element-1926">26,001 pounds or more</strong>, including a
                  towed vehicle with a gross vehicle weight rating of more than{' '}
                  <strong data-id="element-1927">10,000 pounds</strong>
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-1928">
                <h3 className="text-2xl font-bold text-green-600 mb-3" data-id="element-1929">
                  Group B
                </h3>
                <p className="text-gray-700" data-id="element-1930">
                  • A single vehicle having a gross vehicle weight rating of{' '}
                  <strong data-id="element-1931">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-1932">
                <h3 className="text-2xl font-bold text-yellow-600 mb-3" data-id="element-1933">
                  Group C
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-1934">To operate small vehicles:</p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-1935">
                  <li data-id="element-1936">
                    • Designed to carry <strong data-id="element-1937">16 or more people</strong>{' '}
                    including the driver; or
                  </li>
                  <li data-id="element-1938">
                    • Not meeting the requirements for group A or B which carry{' '}
                    <strong data-id="element-1939">hazardous materials</strong> in amounts requiring
                    placarding
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Endorsements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1940">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1941">
              What Endorsements Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-1942">
              In addition to the appropriate CDL group designation, endorsements
              are required for the following:
            </p>

            <div className="space-y-4" data-id="element-1943">
              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1944">
                <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1945">
                  T
                </span>
                <div data-id="element-1946">
                  <p className="font-bold text-gray-900" data-id="element-1947">
                    DOUBLE or TRIPLE TRAILERS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1948">
                    (Triple-trailer combinations are not permitted in Michigan)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1949">
                <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1950">
                  P
                </span>
                <div data-id="element-1951">
                  <p className="font-bold text-gray-900" data-id="element-1952">PASSENGER</p>
                  <p className="text-sm text-gray-600" data-id="element-1953">
                    For vehicles designed to carry more than 15 people
                    (including the driver)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1954">
                <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1955">
                  N
                </span>
                <div data-id="element-1956">
                  <p className="font-bold text-gray-900" data-id="element-1957">TANK VEHICLES</p>
                  <p className="text-sm text-gray-600" data-id="element-1958">
                    For vehicles designed to haul liquids/liquefied gases in
                    permanently mounted tanks of any size or portable tanks
                    rated at 1,000 gallons or more
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1959">
                <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1960">
                  H
                </span>
                <div data-id="element-1961">
                  <p className="font-bold text-gray-900" data-id="element-1962">HAZARDOUS MATERIALS</p>
                  <p className="text-sm text-gray-600" data-id="element-1963">
                    To carry hazardous materials in amounts requiring placarding
                    (includes small trucks, pickup trucks and passenger
                    vehicles)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-1964">
                <span className="bg-orange-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-1965">
                  X
                </span>
                <div data-id="element-1966">
                  <p className="font-bold text-gray-900" data-id="element-1967">
                    Combined TANK and HAZARDOUS MATERIALS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1968">
                    An "X" endorsement appears instead of H and N codes when
                    applicant receives both endorsements
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Driver Qualification File */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1969">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1970">
              Driver Qualification File
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-1971">
              A driver qualification file must contain, but is not limited to:
            </p>

            <div className="space-y-2" data-id="element-1972">
              <div className="flex items-start" data-id="element-1973">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1974" />
                <span className="text-gray-700" data-id="element-1975">
                  A driver's application for employment
                </span>
              </div>
              <div className="flex items-start" data-id="element-1976">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1977" />
                <span className="text-gray-700" data-id="element-1978">
                  A copy of the driver's motor vehicle report
                </span>
              </div>
              <div className="flex items-start" data-id="element-1979">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1980" />
                <span className="text-gray-700" data-id="element-1981">
                  The driver's report of violations (annual)
                </span>
              </div>
              <div className="flex items-start" data-id="element-1982">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1983" />
                <span className="text-gray-700" data-id="element-1984">
                  Employer's review of driving record (annual)
                </span>
              </div>
              <div className="flex items-start" data-id="element-1985">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1986" />
                <span className="text-gray-700" data-id="element-1987">
                  A valid Medical Examiner's Certificate and/or waiver
                </span>
              </div>
              <div className="flex items-start" data-id="element-1988">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-1989" />
                <span className="text-gray-700" data-id="element-1990">
                  Proof of a road test or equivalent (Michigan requires a copy
                  of valid driver's license for intrastate drivers)
                </span>
              </div>
            </div>
          </div>

          {/* Drunk Driving Laws */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-1991">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-1992">
              <AlertTriangle className="mr-3" size={28} data-id="element-1993" />
              Michigan's Drunk Driving Laws Affect CDL License
            </h2>

            <div className="space-y-6" data-id="element-1994">
              <div className="bg-white rounded-lg p-6" data-id="element-1995">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1996">BAC Standards:</h3>
                <p className="text-gray-700 mb-2" data-id="element-1997">
                  FHWA has established <strong data-id="element-1998">0.04%</strong> as the blood
                  alcohol concentration (BAC) level at or above which a CMV
                  driver is deemed to be driving under the influence of alcohol.
                </p>
                <p className="text-sm text-gray-600" data-id="element-1999">
                  States maintain a BAC level between .08% and .10% for non-CMV
                  drivers.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6" data-id="element-2000">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2001">
                  Revocation Periods:
                </h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2002">
                  <li className="flex items-start" data-id="element-2003">
                    <span className="font-bold mr-2 flex-shrink-0" data-id="element-2004">
                      1 year:
                    </span>
                    <span data-id="element-2005">
                      First violation (3 years if transporting hazardous
                      material)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2006">
                    <span className="font-bold mr-2 flex-shrink-0" data-id="element-2007">Life:</span>
                    <span data-id="element-2008">
                      Second violation, regardless of time between violations
                      (possibility of reinstatement after 10 years)
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2009">
                <p className="text-gray-800 mb-3" data-id="element-2010">
                  <strong data-id="element-2011">Important:</strong> These revocations apply to a CDL
                  driver while operating ANY motor vehicle.
                </p>
                <p className="text-sm text-gray-700" data-id="element-2012">
                  When operating a noncommercial motor vehicle, the alcohol
                  content of .08 BAC would be used to determine a drunk driving
                  violation.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6" data-id="element-2013">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2014">
                  Other violations resulting in revocation:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2015">
                  <li data-id="element-2016">• Leaving the scene of an accident</li>
                  <li data-id="element-2017">• Using a vehicle to commit a felony</li>
                  <li data-id="element-2018">• Refusing a request for a chemical test</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600 italic" data-id="element-2019">
                These laws are contained in Act 300 of the Michigan Vehicle
                Code, Sections 257.319b and 257.625. Amendments extending these
                revocations to noncommercial vehicles became effective October
                1, 2005.
              </p>
            </div>
          </div>

          {/* Knowledge & Skills Tests */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2020">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2021">
              Knowledge & Skills Tests
            </h2>

            <div className="space-y-6" data-id="element-2022">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2023">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2024">
                  Knowledge Tests:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2025">
                  <li data-id="element-2026">
                    • The general knowledge test must contain at least{' '}
                    <strong data-id="element-2027">30 questions</strong>
                  </li>
                  <li data-id="element-2028">
                    • To pass, applicants must correctly answer at least{' '}
                    <strong data-id="element-2029">80 percent</strong> of the questions
                  </li>
                  <li data-id="element-2030">
                    • States develop their own tests which must be at least as
                    stringent as Federal standards
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2031">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2032">Skills Tests:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2033">
                  <li data-id="element-2034">
                    • Applicants must successfully perform all required skills
                    (listed in 49 CFR 383.113)
                  </li>
                  <li data-id="element-2035">
                    • Skills test must be taken in a vehicle representative of
                    the type the applicant operates or expects to operate
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-2036">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2037">
                  Third Party Skills Testing:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2038">
                  <li data-id="element-2039">
                    • States, employers, training facilities, and private
                    institutions can serve as third party skills testers
                  </li>
                  <li data-id="element-2040">• Tests must be the same as those given by the State</li>
                  <li data-id="element-2041">
                    • Examiners must meet the same qualifications as State
                    examiners
                  </li>
                  <li data-id="element-2042">
                    • States must conduct on-site inspection at least once a
                    year
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Disqualifications */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2043">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-2044">
              <Shield className="mr-3" size={32} data-id="element-2045" />
              Disqualifications
            </h2>

            <p className="text-lg mb-6" data-id="element-2046">
              For conviction while driving a CMV, drivers must be disqualified
              and lose their privilege to drive:
            </p>

            <div className="space-y-4" data-id="element-2047">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2048">
                <p className="text-2xl font-bold mb-2" data-id="element-2049">60 to 120 days</p>
                <p className="text-gray-200" data-id="element-2050">
                  Two or more serious traffic violations within a 3-year period
                  (excessive speeding, reckless driving, improper lane changes,
                  following too closely, traffic offenses in fatal accidents)
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2051">
                <p className="text-2xl font-bold mb-2" data-id="element-2052">90 days to 5 years</p>
                <p className="text-gray-200" data-id="element-2053">
                  One or more violations of an out-of-service order within a
                  10-year period
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2054">
                <p className="text-2xl font-bold mb-2" data-id="element-2055">1 Year</p>
                <ul className="text-gray-200 space-y-1" data-id="element-2056">
                  <li data-id="element-2057">
                    • Driving under the influence of controlled substance or
                    alcohol
                  </li>
                  <li data-id="element-2058">• Leaving the scene of an accident</li>
                  <li data-id="element-2059">• Using a CMV to commit a felony</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2060">
                <p className="text-2xl font-bold mb-2" data-id="element-2061">3 Years</p>
                <p className="text-gray-200" data-id="element-2062">
                  Any of the 1-year offenses while operating a CMV that is
                  placarded for hazardous materials
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2063">
                <p className="text-2xl font-bold mb-2" data-id="element-2064">Life</p>
                <ul className="text-gray-200 space-y-1" data-id="element-2065">
                  <li data-id="element-2066">
                    • Second offense of any of the 1-year or 3-year offenses
                  </li>
                  <li data-id="element-2067">
                    • Using a CMV to commit a felony involving manufacturing,
                    distributing, or dispensing controlled substances
                  </li>
                </ul>
                <p className="text-sm text-gray-300 mt-2" data-id="element-2068">
                  States have option to reduce certain lifetime
                  disqualifications to minimum of 10 years if driver completes
                  approved rehabilitation program
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-2069">
              <p className="font-semibold mb-2" data-id="element-2070">Important Notes:</p>
              <ul className="space-y-2 text-sm" data-id="element-2071">
                <li data-id="element-2072">
                  • If disqualified from operating a CMV, State may issue
                  license to operate non-CMVs
                </li>
                <li data-id="element-2073">
                  • Drivers disqualified from CMV cannot be issued "conditional"
                  or "hardship" CDL
                </li>
                <li data-id="element-2074">
                  • Out-of-state violations treated same as home State
                  convictions
                </li>
              </ul>
            </div>
          </div>

          {/* Employer Notifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2075">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2076">
              Employer Notifications
            </h2>

            <div className="space-y-4" data-id="element-2077">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2078">
                <p className="text-gray-800 mb-2" data-id="element-2079">
                  <strong data-id="element-2080">Within 30 days of a conviction:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2081">
                  Driver must notify employer of any traffic violation (except
                  parking), regardless of nature of violation or type of vehicle
                  driven
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2082">
                <p className="text-gray-800 mb-2" data-id="element-2083">
                  <strong data-id="element-2084">By end of next business day:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2085">
                  If license is suspended, revoked, canceled, or driver is
                  disqualified, employer must be notified
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-2086">
                <p className="text-gray-800 mb-2" data-id="element-2087">
                  <strong data-id="element-2088">Employer Requirements:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2089">
                  Employers may not knowingly use a driver who has more than one
                  license or whose license is suspended, revoked, canceled, or
                  disqualified. Violation may result in civil or criminal
                  penalties.
                </p>
              </div>
            </div>
          </div>

          {/* Penalties */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2090">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2091">Penalties</h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-2092">
              <div className="bg-red-50 rounded-lg p-6" data-id="element-2093">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2094">
                  Driver Penalties:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2095">
                  <li data-id="element-2096">
                    • Civil penalty: up to <strong data-id="element-2097">$2,500</strong>
                  </li>
                  <li data-id="element-2098">
                    • Criminal penalties (aggravated cases): up to{' '}
                    <strong data-id="element-2099">$5,000</strong> in fines and/or up to{' '}
                    <strong data-id="element-2100">90 days</strong> in prison
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6" data-id="element-2101">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2102">
                  Employer Penalties:
                </h3>
                <p className="text-gray-700" data-id="element-2103">
                  Subject to penalty of up to <strong data-id="element-2104">$10,000</strong> if
                  knowingly using a driver to operate a CMV without a valid CDL
                </p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2105">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2106">
              Current Status of the CDL Program
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2107">
              Over 8 million drivers have passed the knowledge and skills tests
              and obtained a CDL. Approximately 11 percent of these CDL drivers
              have been disqualified at least once during the period of April
              1992 through June 1996.
            </p>

            <div className="space-y-4" data-id="element-2108">
              <div data-id="element-2109">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2110">
                  Current Enhancements:
                </h3>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2111">
                  <li data-id="element-2112">• Driver Data Exchange With Canada and Mexico</li>
                  <li data-id="element-2113">• CDL Judicial Outreach Project (JOP)</li>
                </ul>
              </div>

              <div data-id="element-2114">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2115">
                  Future Enhancements Being Considered:
                </h3>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2116">
                  <li data-id="element-2117">• Graduated Commercial Licenses</li>
                  <li data-id="element-2118">• Third Party CDL Knowledge Testing</li>
                  <li data-id="element-2119">
                    • Merging Medical Fitness Determination Into CDL Process
                  </li>
                  <li data-id="element-2120">• Simulator Validation for Training & Testing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2121">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2122">
              Need Help with CDL Testing & Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-2123">
              Expert guidance on CDL testing, qualifications, and compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2124">
              <Phone className="mr-3" size={24} data-id="element-2125" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2126">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}